import React, { useState } from 'react';
import api, { BASE as API_BASE } from '../services/api';
import { toast } from '../utils/toastBus';
import { Button, Card, EmptyState } from '../components/ui';

export default function Transcribe() {
  const [sourceUrl, setSourceUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null); // { id, text, language, confidence }

  async function onSubmit(e) {
    e?.preventDefault?.();
    const url = String(sourceUrl || '').trim();
    if (!url) {
      toast({ type: 'error', message: 'Enter a source URL to transcribe' });
      return;
    }
    try {
      setLoading(true);
      setResult(null);
      const res = await api.transcribe({ sourceUrl: url });
      if (res?.error) throw new Error(res.error);
      const id = res?.id ?? res?.item?.id ?? null;
      const text = res?.item?.text ?? '';
      const language = res?.item?.language ?? null;
      const confidence = res?.item?.confidence ?? null;
      setResult({ id, text, language, confidence });
      toast({ type: 'success', message: 'Transcription complete' });
    } catch (e) {
      toast({ type: 'error', message: e?.message || 'Transcription failed' });
    } finally {
      setLoading(false);
    }
  }

  async function onFileUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    
    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await api.uploadFile(formData);
      if (res?.url) {
        setSourceUrl(res.url);
        toast({ type: 'success', message: 'File uploaded! Click Transcribe to start.' });
      } else {
        throw new Error(res?.error || 'Upload failed');
      }
    } catch (err) {
      toast({ type: 'error', message: err?.message || 'Upload failed' });
    } finally {
      setUploading(false);
    }
  }

  function normalizeUrl(u) {
    if (!u || typeof u !== 'string') return '';
    if (u.startsWith('/uploads/')) return `${API_BASE}${u}`;
    return u;
  }

  return (
    <div style={{ padding: 24 }}>
      <header style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
        <h2 style={{ color: '#ff7a45', margin: 0 }}>Transcribe</h2>
      </header>

      <Card title="Transcribe Audio">
        <p style={{ color: '#9ca3af', marginTop: 0, marginBottom: 16 }}>
          Upload an audio file or paste a URL to convert speech to text
        </p>
        
        {/* File Upload */}
        <div style={{ marginBottom: 16 }}>
          <label 
            htmlFor="transcribe-file-upload"
            style={{
              display: 'block',
              padding: '24px',
              border: '2px dashed #374151',
              borderRadius: 8,
              textAlign: 'center',
              cursor: 'pointer',
              background: uploading ? 'rgba(255, 90, 31, 0.1)' : 'transparent',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => e.target.style.borderColor = '#ff5a1f'}
            onMouseLeave={(e) => e.target.style.borderColor = '#374151'}
          >
            <div style={{ fontSize: 32, marginBottom: 8 }}>
              {uploading ? '⏳' : '📁'}
            </div>
            <div style={{ color: '#e5e7eb', fontWeight: 500, marginBottom: 4 }}>
              {uploading ? 'Uploading...' : 'Click to upload audio file'}
            </div>
            <div style={{ color: '#9ca3af', fontSize: 14 }}>
              or drag and drop here
            </div>
            <input
              id="transcribe-file-upload"
              type="file"
              accept="audio/*,video/*"
              onChange={onFileUpload}
              disabled={uploading}
              style={{ display: 'none' }}
            />
          </label>
        </div>

        {/* Divider */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 12, 
          margin: '16px 0',
          color: '#6b7280',
          fontSize: 14
        }}>
          <div style={{ flex: 1, height: 1, background: '#374151' }} />
          <span>OR</span>
          <div style={{ flex: 1, height: 1, background: '#374151' }} />
        </div>

        {/* URL Input */}
        <form onSubmit={onSubmit} style={{ display: 'flex', gap: 8 }}>
          <input
            type="text"
            placeholder="https://... or /uploads/yourfile.mp3"
            value={sourceUrl}
            onChange={(e) => setSourceUrl(e.target.value)}
            style={{ 
              flex: 1, 
              padding: '10px 12px', 
              borderRadius: 8, 
              border: '1px solid #374151', 
              background: '#0b0b0e', 
              color: '#e5e7eb',
              fontSize: 16
            }}
          />
          <Button type="submit" loading={loading} disabled={loading || uploading}>
            {loading ? 'Transcribing…' : 'Transcribe'}
          </Button>
        </form>
      </Card>

      {!result && !loading && (
        <EmptyState
          icon="🎤"
          title="No transcription yet"
          description="Enter an audio URL above and click Transcribe to convert speech to text"
        />
      )}

      {result && (
        <Card title="Transcription Result">
          <div style={{ color: '#9ca3af', marginBottom: 8 }}>
            {result.language ? (<span>Language: {result.language} · </span>) : null}
            {typeof result.confidence === 'number' ? (<span>Confidence: {Math.round(result.confidence * 100)}%</span>) : null}
          </div>
          <div style={{ whiteSpace: 'pre-wrap', color: '#e5e7eb', marginTop: 12 }}>{result.text || '(empty)'}</div>
          <div style={{ marginTop: 16, display: 'flex', gap: 12 }}>
            {result.id ? (
              <a href={`${API_BASE}/api/media/transcripts/${encodeURIComponent(String(result.id))}`} target="_blank" rel="noreferrer" style={{ color: '#60a5fa' }}>
                Open transcript JSON
              </a>
            ) : null}
            {sourceUrl ? (
              <a href={normalizeUrl(sourceUrl)} target="_blank" rel="noreferrer" style={{ color: '#34d399' }}>
                Open source
              </a>
            ) : null}
          </div>
        </Card>
      )}
    </div>
  );
}
